// src/views/dashboard/salesrep/SalesRepAdvancedCareDashboard.jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  IoChevronDown, 
  IoCloseOutline, 
  IoCheckmarkCircle, 
  IoAddCircleOutline, 
  IoMedkitOutline,
  IoArrowBack,
  IoArrowForward,
  IoResizeOutline
} from "react-icons/io5";
import PendingIVROrdersSection from "../advancedcare/PendingIVROrdersSection";

// ✅ API Configuration
const API_BASE_URL = "https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/";

const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const reorderCareKit = async (orderId, overrides = {}) => {
  const response = await fetch(`${API_BASE_URL}/${orderId}/reorder/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(overrides),
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.errors ? JSON.stringify(data.errors) : 'Failed to reorder');
  }
  
  return data;
};

const fetchOrderDetails = async (orderId) => {
  const response = await fetch(`${API_BASE_URL}/carekit/${orderId}/`, {
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch order details');
  }
  
  return await response.json();
};

const SalesRepAdvancedCareDashboard = ({ 
  source, 
  currentNavItem, 
  onNavigateToAddPatient,
  onNavigateToReorder  // ✅ NEW: Callback for navigating to reorder with different size
}) => {
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [stats, setStats] = useState({
    total_orders: 0,
    by_care_type: { conservative: 0, advanced: 0 },
    by_status: {},
    orders_over_time: []
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(false);
  
  // ✅ Reorder state
  const [reorderConfirmModalOpen, setReorderConfirmModalOpen] = useState(false);
  const [reorderOrderId, setReorderOrderId] = useState(null);
  const [reorderStep, setReorderStep] = useState(1); // 1: confirm reorder, 2: same size or different
  const [currentView, setCurrentView] = useState('dashboard');
  const [allPatients, setAllPatients] = useState([]);
  // const [selectedPendingOrdersProvider, setSelectedPendingOrdersProvider] = useState(null);

  const [pendingOrdersProviders, setPendingOrdersProviders] = useState([]);
  const [loadingPendingOrdersProviders, setLoadingPendingOrdersProviders] = useState(false);
  const [selectedPendingOrdersProvider, setSelectedPendingOrdersProvider] = useState(null);


  // Fetch providers list on mount
  useEffect(() => {
    fetchProviders();
  }, []);

  // Fetch data when provider or source changes
  useEffect(() => {
    if (selectedProvider && currentNavItem === 'patients') {
      fetchProviderData();
    }
  }, [selectedProvider, source, currentNavItem]);

  
  useEffect(() => {
  if (currentNavItem === 'pendingPartialOrders') {
    console.log('📋 Navigating to pending orders - fetching providers');
    setCurrentView('pendingOrdersProviderSelection');
    setSelectedPendingOrdersProvider(null); // Reset selection
    fetchPendingOrdersProviders(); // ✅ Fetch providers for selection
  } else if (currentNavItem === 'patients') {
    setCurrentView('dashboard');
  }
}, [currentNavItem]);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/sales-rep/providers/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setProviders(data.data);
        if (data.data.length > 0) {
          setSelectedProvider(data.data[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching providers:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingOrdersProviders = async () => {
  try {
    setLoadingPendingOrdersProviders(true);
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/sales-rep/providers/`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    
    const data = await response.json();
    
    if (data.success) {
      setPendingOrdersProviders(data.data);
      console.log('✅ Loaded providers for pending orders selection:', data.data.length);
      // ✅ IMPORTANT: Do NOT auto-select - user must choose manually
    }
  } catch (error) {
    console.error("Error fetching pending orders providers:", error);
  } finally {
    setLoadingPendingOrdersProviders(false);
  }
};

// ✅ Add this function after fetchPendingOrdersProviders
const fetchPatientsForPendingOrdersProvider = async (providerId) => {
  try {
    const token = localStorage.getItem('accessToken');
    const patientsResponse = await fetch(
      `${API_BASE_URL}/provider-patients/?provider_id=${providerId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      }
    );
    
    const patientsData = await patientsResponse.json();
    
    if (patientsData.success) {
      console.log('✅ Fetched patients for pending orders provider:', patientsData.data.length);
      setAllPatients(patientsData.data);
    } else {
      console.error('❌ Failed to fetch patients:', patientsData.error);
      setAllPatients([]);
    }
  } catch (error) {
    console.error('❌ Error fetching patients:', error);
    setAllPatients([]);
  }
};

  const fetchProviderData = async () => {
    if (!selectedProvider) return;

    try {
      setLoadingOrders(true);
      const token = localStorage.getItem('accessToken');
      
      const careTypeParam = source === 'conservative_care' ? 'conservative' : 'advanced';
      
      const statsResponse = await fetch(
        `${API_BASE_URL}/sales-rep/stats/?provider_id=${selectedProvider.id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );
      const statsData = await statsResponse.json();
      
      if (statsData.success) {
        setStats(statsData.stats);
      }

      const ordersResponse = await fetch(
        `${API_BASE_URL}/provider-recent/?provider_id=${selectedProvider.id}&limit=10&care_type=${careTypeParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );
      
      const ordersData = await ordersResponse.json();
      
      if (ordersData.success) {
        console.log('✅ Fetched orders for provider:', ordersData.count);
        setRecentOrders(ordersData.data);
      } else {
        console.error('❌ Failed to fetch orders:', ordersData.error);
        setRecentOrders([]);
      }

      const fetchProviderData = async () => {
  if (!selectedProvider) return;

  try {
    setLoadingOrders(true);
    const token = localStorage.getItem('accessToken');
    
    const careTypeParam = source === 'conservative_care' ? 'conservative' : 'advanced';
    
    // ✅ Fetch stats
    const statsResponse = await fetch(
      `${API_BASE_URL}/sales-rep/stats/?provider_id=${selectedProvider.id}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      }
    );
    const statsData = await statsResponse.json();
    
    if (statsData.success) {
      setStats(statsData.stats);
    }

    // ✅ Fetch recent orders
    const ordersResponse = await fetch(
      `${API_BASE_URL}/provider-recent/?provider_id=${selectedProvider.id}&limit=10&care_type=${careTypeParam}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      }
    );
    
    const ordersData = await ordersResponse.json();
    
    if (ordersData.success) {
      console.log('✅ Fetched orders for provider:', ordersData.count);
      setRecentOrders(ordersData.data);
    } else {
      console.error('❌ Failed to fetch orders:', ordersData.error);
      setRecentOrders([]);
    }
    
    // ✅ NEW: Fetch all patients for this provider
    const patientsResponse = await fetch(
      `${API_BASE_URL}/provider-patients/?provider_id=${selectedProvider.id}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      }
    );
    
    const patientsData = await patientsResponse.json();
    
    if (patientsData.success) {
      console.log('✅ Fetched patients for provider:', patientsData.data.length);
      setAllPatients(patientsData.data);
    }
    
  } catch (error) {
    console.error("Error fetching provider data:", error);
    setRecentOrders([]);
    setAllPatients([]);
  } finally {
    setLoadingOrders(false);
  }
};
      
    } catch (error) {
      console.error("Error fetching provider data:", error);
      setRecentOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  // ✅ Handle reorder with same size
  const handleReorderSameSize = async (orderId) => {
    try {
      setLoading(true);
      
      const overrides = {};
      
      if (selectedProvider) {
        overrides.provider_id = selectedProvider.id;
        console.log('🔑 Sales rep reorder - Including provider_id:', selectedProvider.id);
      }
      
      const result = await reorderCareKit(orderId, overrides);
      console.log('Reorder created successfully:', result);
      
      await fetchProviderData();
      setCurrentView('reorderSuccess');
      
    } catch (err) {
      console.error('Error creating reorder:', err);
      alert(`Failed to create reorder: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle reorder with different size
  const handleReorderDifferentSize = async (orderId) => {
    try {
      setLoading(true);
      
      // Fetch the original order details
      const orderDetails = await fetchOrderDetails(orderId);
      
      console.log('📦 Fetched order details for modification:', orderDetails);
      
      // Navigate to the reorder flow with order data
      if (onNavigateToReorder) {
        onNavigateToReorder({
          orderData: orderDetails,
          providerId: selectedProvider.id
        });
      }
      
    } catch (err) {
      console.error('Error fetching order for reorder:', err);
      alert(`Failed to load order details: ${err.message}`);
    } finally {
      setLoading(false);
      setReorderConfirmModalOpen(false);
      setReorderStep(1);
    }
  };

  const getServiceTitle = () => {
    return source === 'conservative_care' ? 'Conservative Care' : 'Advanced Care';
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'cancelled':
      case 'refunded':
      case 'failed':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'pending':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      case 'processing':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'shipped':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  // ✅ Updated Reorder Confirmation Modal with Size Selection
  const ReorderConfirmationModal = () => (
    <AnimatePresence>
      {reorderConfirmModalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => {
            if (!loading) {
              setReorderConfirmModalOpen(false);
              setReorderStep(1);
            }
          }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-md w-full mx-4 p-8 border border-gray-200 dark:border-gray-700"
          >
            {/* Close button */}
            <button
              onClick={() => {
                if (!loading) {
                  setReorderConfirmModalOpen(false);
                  setReorderStep(1);
                }
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              disabled={loading}
            >
              <IoCloseOutline className="text-2xl" />
            </button>

            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-900/30 dark:to-cyan-900/30 flex items-center justify-center">
                {reorderStep === 1 ? (
                  <IoMedkitOutline className="text-4xl text-blue-600 dark:text-blue-400" />
                ) : (
                  <IoResizeOutline className="text-4xl text-purple-600 dark:text-purple-400" />
                )}
              </div>
            </div>

            {/* Step 1: Confirm Reorder */}
            {reorderStep === 1 && (
              <>
                <h2 className="text-2xl font-black text-gray-900 dark:text-white text-center mb-2">
                  Confirm Reorder
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-center mb-8">
                  Do you want to create a reorder for this patient?
                </p>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-6">
                  <p className="text-sm text-blue-800 dark:text-blue-300 font-medium text-center">
                    You can reorder with the same configuration or modify the kit size/graft.
                  </p>
                </div>

                <div className="flex flex-col gap-3">
                  <motion.button
                    onClick={async () => {
                      const userRole = localStorage.getItem('userRole');
                      
                      // ✅ Find the order being reordered to get patient info
                      const orderToReorder = recentOrders.find(o => o.id === reorderOrderId);

                      console.log(orderToReorder);
                      
                      if (orderToReorder && onNavigateToAddPatient) {
                        console.log('🔄 Sales Rep - Creating new order for patient:', orderToReorder.patient_name);
                        
                        try {
                          // ✅ NEW: Fetch the full patient object from the backend
                          const token = localStorage.getItem('accessToken');
                          const patientResponse = await fetch(`${API_BASE_URL}/patients/${orderToReorder.patient}/`, {
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json',
                            }
                          });
                          
                          if (!patientResponse.ok) {
                            throw new Error('Failed to fetch patient details');
                          }
                          
                          const patientData = await patientResponse.json();
                          console.log('✅ Fetched full patient data:', patientData);
                          
                          // ✅ Call onNavigateToAddPatient with complete patient data
                          onNavigateToAddPatient({
                            patientId: orderToReorder.patient,
                            patient: patientData, // ✅ Pass the full patient object
                            patientName: orderToReorder.patient_name,
                            careType: orderToReorder.conservative_care ? 'propacks' : 'advanced',
                            skipToStep2: true,
                            providerId: orderToReorder.provider_id || selectedProvider?.id
                          });
                          
                        } catch (err) {
                          console.error('❌ Error fetching patient for reorder:', err);
                          alert('Failed to load patient details. Please try again.');
                          return;
                        }
                      }
                      
                      setReorderConfirmModalOpen(false);
                      setReorderStep(1);
                      setReorderOrderId(null);
                    }}
                    disabled={loading}
                    className="w-full px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-xl font-bold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <IoAddCircleOutline className="text-xl" />
                    No, New Order
                  </motion.button>
                  
                  <motion.button
                    onClick={() => setReorderStep(2)}
                    disabled={loading}
                    className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-xl font-bold transition-all disabled:opacity-50 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <IoCheckmarkCircle className="text-xl" />
                    Yes, Reorder
                  </motion.button>
                </div>
              </>
            )}

            {/* Step 2: Same Size or Different Size */}
            {reorderStep === 2 && (
              <>
                <h2 className="text-2xl font-black text-gray-900 dark:text-white text-center mb-2">
                  Reorder with Same Size?
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-center mb-8">
                  Would you like to reorder with the same kit size/graft or modify it?
                </p>

                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4 mb-6">
                  <p className="text-sm text-purple-800 dark:text-purple-300 font-medium text-center">
                    Select "Different Size" to customize the ProPack configuration or graft selection.
                  </p>
                </div>

                <div className="flex flex-col gap-3">
                  <motion.button
                    onClick={() => setReorderStep(1)}
                    disabled={loading}
                    className="w-full px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-xl font-bold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <IoArrowBack className="text-xl" />
                    Back
                  </motion.button>

                  <motion.button
                    onClick={() => handleReorderDifferentSize(reorderOrderId)}
                    disabled={loading}
                    className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl font-bold transition-all disabled:opacity-50 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {loading ? (
                      <>
                        <motion.div
                          className="w-5 h-5 border-3 border-white border-t-transparent rounded-full"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                        Loading...
                      </>
                    ) : (
                      <>
                        <IoResizeOutline className="text-xl" />
                        Different Size
                      </>
                    )}
                  </motion.button>
                  
                  <motion.button
                    onClick={async () => {
                      await handleReorderSameSize(reorderOrderId);
                      setReorderConfirmModalOpen(false);
                      setReorderStep(1);
                      setReorderOrderId(null);
                    }}
                    disabled={loading}
                    className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-xl font-bold transition-all disabled:opacity-50 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {loading ? (
                      <>
                        <motion.div
                          className="w-5 h-5 border-3 border-white border-t-transparent rounded-full"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                        Processing...
                      </>
                    ) : (
                      <>
                        <IoCheckmarkCircle className="text-xl" />
                        Same Size
                      </>
                    )}
                  </motion.button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // ============================================================================
// RENDER PROVIDER SELECTION FOR PENDING ORDERS
// ============================================================================
const renderPendingOrdersProviderSelection = () => {
  // ✅ Use pending orders specific loading state
  if (loadingPendingOrdersProviders) {
    return (
      <div className="min-h-[400px] px-4 sm:px-6 lg:px-12 pt-24 pb-10">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-3xl border border-slate-200/70 bg-white/80 p-10 text-center shadow-sm backdrop-blur dark:border-slate-700/60 dark:bg-slate-900/60">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full border-4 border-teal-200/70 border-t-teal-600 dark:border-teal-900/40 dark:border-t-teal-400 animate-spin" />
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Loading providers…
            </p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Fetching your assigned network
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ✅ Use pending orders specific providers list
  if (pendingOrdersProviders.length === 0) {
    return (
      <div className="px-4 sm:px-6 lg:px-12 pt-24 pb-10">
        <div className="max-w-2xl mx-auto">
          <div className="rounded-3xl border border-amber-200/70 bg-amber-50/80 p-10 text-center shadow-sm backdrop-blur dark:border-amber-500/20 dark:bg-amber-950/20">
            <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-2xl bg-amber-500/15 text-3xl ring-1 ring-amber-500/20 dark:bg-amber-400/10 dark:ring-amber-400/20">
              👥
            </div>
            <h3 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">
              No providers assigned
            </h3>
            <p className="mt-2 text-sm font-medium text-amber-800/90 dark:text-amber-200/80">
              You don&apos;t have any providers yet. Contact your administrator to get started.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-12 pt-24 pb-10">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -14 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                Select Provider
              </h1>
              <p className="mt-1 text-sm sm:text-base text-slate-600 dark:text-slate-400">
                Choose a provider to view pending IVR approvals.
              </p>
            </div>

            <div className="hidden sm:flex items-center gap-2 rounded-2xl border border-slate-200/70 bg-white/70 px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm backdrop-blur dark:border-slate-700/60 dark:bg-slate-900/50 dark:text-slate-300">
              <span className="inline-flex h-2 w-2 rounded-full bg-teal-500" />
              Updated live
            </div>
          </div>

          {/* Summary Card */}
          <div className="mt-5 rounded-3xl border border-slate-200/70 bg-white/80 p-5 shadow-sm backdrop-blur dark:border-slate-700/60 dark:bg-slate-900/60">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-teal-600 to-emerald-600 text-white shadow-lg grid place-items-center">
                  <IoMedkitOutline className="text-2xl" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Providers available
                  </p>
                  <p className="mt-1 text-lg font-extrabold text-slate-900 dark:text-white">
                    {pendingOrdersProviders.length}{" "}
                    <span className="font-bold text-slate-700 dark:text-slate-300">
                      Provider{pendingOrdersProviders.length !== 1 ? "s" : ""}
                    </span>
                  </p>
                </div>
              </div>

              <div className="hidden md:flex items-center gap-2 rounded-2xl border border-teal-200/50 bg-teal-50/60 px-3 py-2 text-xs font-semibold text-teal-800 dark:border-teal-500/20 dark:bg-teal-950/20 dark:text-teal-200">
                Tip: pick a provider to filter orders
              </div>
            </div>
          </div>
        </motion.div>

        {/* Providers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pendingOrdersProviders.map((provider, index) => (
            <motion.button
              key={provider.id}
              onClick={async () => {
                console.log("✅ Provider selected:", provider.name, provider.id);
                setSelectedPendingOrdersProvider(provider);
                await fetchPatientsForPendingOrdersProvider(provider.id);
                setCurrentView("pendingPartialOrders");
              }}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.04 }}
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.99 }}
              className="group relative overflow-hidden rounded-3xl border border-slate-200/70 bg-white/80 p-6 text-left shadow-sm backdrop-blur transition-all hover:shadow-lg dark:border-slate-700/60 dark:bg-slate-900/60"
            >
              {/* Hover wash */}
              <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 via-transparent to-emerald-500/10 dark:from-teal-400/10 dark:to-emerald-400/10" />
              </div>

              <div className="relative z-10 flex items-center gap-4">
                {/* Avatar */}
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-600 to-emerald-600 text-white shadow-lg ring-1 ring-black/5 dark:ring-white/10">
                  <span className="text-xl font-extrabold">
                    {provider.name?.charAt(0)?.toUpperCase() || "P"}
                  </span>
                </div>

                {/* Info */}
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-lg font-extrabold text-slate-900 dark:text-white">
                    {provider.name}
                  </h3>
                  <p className="mt-0.5 truncate text-sm text-slate-600 dark:text-slate-400">
                    {provider.email}
                  </p>

                  {provider.facility && (
                    <div className="mt-2 inline-flex max-w-full items-center gap-2 rounded-full border border-slate-200/60 bg-slate-50/70 px-3 py-1 text-xs font-semibold text-slate-700 dark:border-slate-700/60 dark:bg-slate-800/40 dark:text-slate-300">
                      <span>🏥</span>
                      <span className="truncate">{provider.facility}</span>
                    </div>
                  )}
                </div>

                {/* Arrow */}
                <div className="flex-shrink-0">
                  <div className="grid h-10 w-10 place-items-center rounded-2xl border border-slate-200/70 bg-white/70 text-slate-500 transition-all group-hover:border-teal-300/60 group-hover:text-teal-700 dark:border-slate-700/60 dark:bg-slate-900/40 dark:text-slate-400 dark:group-hover:border-teal-500/30 dark:group-hover:text-teal-300">
                    <IoArrowForward className="text-xl transition-transform group-hover:translate-x-0.5" />
                  </div>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
};


  // ... rest of your existing code (renderReorderSuccess, renderDashboard, etc.)
  
  const renderReorderSuccess = () => (
    <motion.div
      className="px-4 sm:px-6 lg:px-12 pb-8 min-h-[60vh] flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-2xl w-full">
        <motion.div
          className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 p-12 relative overflow-hidden"
          initial={{ scale: 0.8, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 150, damping: 15 }}
        >
          <div className="relative z-10 text-center">
            <motion.div
              className="mb-8 inline-block"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 10, delay: 0.2 }}
            >
              <div className="w-32 h-32 mx-auto bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-2xl">
                <IoCheckmarkCircle className="text-7xl text-white" />
              </div>
            </motion.div>

            <h2 className="text-5xl font-black text-gray-900 dark:text-white mb-3">
              🎉 Reorder Successful!
            </h2>
            
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-10 font-semibold">
              Your CareKit reorder has been created successfully.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <motion.button
                onClick={() => {
                  if (onNavigateToAddPatient) {
                    onNavigateToAddPatient();
                  }
                  setCurrentView('dashboard');
                }}
                className="flex-1 px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-2xl font-bold transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <IoAddCircleOutline className="text-2xl" />
                Create Another Order
              </motion.button>

              <motion.button
                onClick={() => setCurrentView('dashboard')}
                className="flex-1 px-8 py-4 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-2xl font-bold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all flex items-center justify-center gap-2"
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <IoArrowBack className="text-xl rotate-180" />
                Back to Dashboard
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );

  const renderDashboard = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-teal-200 dark:border-teal-800 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading providers...</p>
          </div>
        </div>
      );
    }

    if (providers.length === 0) {
      return (
        <div className="px-4 sm:px-6 lg:px-12 py-8">
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg">
            <p className="text-amber-800 font-semibold">No providers assigned to you yet.</p>
            <p className="text-amber-700 text-sm mt-2">Contact your administrator to get providers assigned.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="px-4 sm:px-6 lg:px-12 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {getServiceTitle()} Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Overview of {getServiceTitle().toLowerCase()} orders and activity
          </p>

          <div className="max-w-md">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Select Provider
            </label>
            <div className="relative">
              <select
                value={selectedProvider?.id || ''}
                onChange={(e) => {
                  const provider = providers.find(p => p.id === parseInt(e.target.value));
                  setSelectedProvider(provider);
                }}
                className="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white font-semibold focus:ring-4 focus:ring-teal-500/20 focus:border-teal-500 transition-all appearance-none cursor-pointer"
              >
                {providers.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name} ({provider.email})
                  </option>
                ))}
              </select>
              <IoChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
            </div>
          </div>
        </div>

        {loadingOrders ? (
          <div className="flex justify-center items-center min-h-[300px]">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-teal-200 dark:border-teal-800 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Loading {getServiceTitle()} data...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mb-8">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Orders</span>
                </div>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {source === 'conservative_care' ? stats.by_care_type.conservative : stats.by_care_type.advanced}
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending</span>
                </div>
                <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">
                  {stats.by_status.pending || 0}
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Processing</span>
                </div>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {stats.by_status.processing || 0}
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Delivered</span>
                </div>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {stats.by_status.delivered || 0}
                </p>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                {getServiceTitle()} Recent Activity
              </h2>

              {recentOrders.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 font-medium">No recent {getServiceTitle().toLowerCase()} orders</p>
                  <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Orders will appear here once they're placed</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">ORDER #</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">PATIENT</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">KIT SELECTED</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">STATUS</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">DATE</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">ACTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOrders.map((order) => (
                        <tr key={order.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <td className="py-4 px-4 text-sm font-medium text-gray-900 dark:text-white">
                            {order.order_number}
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                {order.patient_name?.charAt(0) || 'P'}
                              </div>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {order.patient_name || 'N/A'}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700 dark:text-gray-300">
                            {order.kit_size_display || 'N/A'}
                          </td>
                          <td className="py-4 px-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                              {order.status_display || order.status}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700 dark:text-gray-300">
                            {new Date(order.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-4 px-4">
                            <button 
                              onClick={() => {
                                setReorderOrderId(order.id);
                                setReorderStep(1);
                                setReorderConfirmModalOpen(true);
                              }}
                              disabled={loading}
                              className="px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
                            >
                              Reorder
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  // ============================================================================
// RENDER PENDING ORDERS PAGE - FULL PAGE VIEW FOR SALES REP
// ============================================================================
// ============================================================================
// RENDER PENDING ORDERS PAGE - FULL PAGE VIEW FOR SALES REP
// ============================================================================
  const renderPendingOrdersPage = () => {
  // ✅ Use pending orders specific provider state
  if (!selectedPendingOrdersProvider) {
    return renderPendingOrdersProviderSelection();
  }

  return (
    <div className="w-full bg-gradient-to-br from-white via-gray-50/50 to-teal-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 min-h-screen">
      <div className="px-4 sm:px-6 lg:px-12 pb-8 pt-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Page Header with Provider Info */}
          <div className="mb-6">
            {/* Back Button */}
            <motion.button
              onClick={() => {
                console.log('⬅️ Going back to provider selection');
                setSelectedPendingOrdersProvider(null);
                setCurrentView('pendingOrdersProviderSelection');
              }}
              className="flex items-center gap-2 text-amber-600 dark:text-amber-400 hover:gap-3 transition-all font-bold mb-4 group"
              whileHover={{ x: -5 }}
            >
              <IoArrowBack className="text-xl group-hover:scale-110 transition-transform" />
              Back to Provider Selection
            </motion.button>

            {/* Provider Info Card */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-5 mb-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-white font-black text-2xl shadow-lg">
                  {selectedPendingOrdersProvider.name?.charAt(0) || 'P'}
                </div>
                <div>
                  <h2 className="text-2xl font-black text-gray-900">
                    {selectedPendingOrdersProvider.name}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {selectedPendingOrdersProvider.email}
                    {selectedPendingOrdersProvider.facility && (
                      <span className="ml-2">• 🏥 {selectedPendingOrdersProvider.facility}</span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-2 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
              Pending IVR Approvals
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Orders waiting for IVR approval from this provider
            </p>
          </div>

          {/* Pending Orders Component */}
          <PendingIVROrdersSection
            API_BASE_URL={API_BASE_URL}
            getAuthToken={() => localStorage.getItem('accessToken')}
            providerId={selectedPendingOrdersProvider.id}
            onCompleteOrder={async (order) => {
              const capitalizeFirst = (str) => {
                if (!str) return 'None';
                return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
              };

              const patient = allPatients.find(p => p.id === order.patient_id);

              if (!patient) {
                alert('Patient not found. Please refresh and try again.');
                return;
              }

              if (order.care_type === 'propacks') {
                try {
                  const token = localStorage.getItem('accessToken');
                  const response = await fetch(
                    `${API_BASE_URL}/patients/${order.patient_id}/ivr-forms/`,
                    {
                      headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                      }
                    }
                  );
                  const data = await response.json();
                  console.log('📋 IVR forms response:', data);  // ← add this
                  const ivrForms = Array.isArray(data) ? data : data.results || [];
                  console.log('📋 IVR forms:', ivrForms);  // ← and this
                  const latestIvr = ivrForms.find(f => f.status === 'approved' && f.form_data) 
                    || ivrForms.find(f => f.form_data)
                    || ivrForms[0];
                  console.log('📋 Latest IVR:', latestIvr);  // ← and this
                  console.log('📦 Full order object:', order);
                  if (!latestIvr?.form_data) {
                    alert('Could not retrieve IVR form data. Please contact support.');
                    return;
                  }

                  if (onNavigateToAddPatient) {
                    onNavigateToAddPatient({
                      patientId: order.patient_id,
                      patient: patient,
                      careType: order.care_type,
                      providerId: selectedPendingOrdersProvider.id,
                      partialOrderId: order.id,
                      isCompletingPartialOrder: true,
                      skipToStep3: true,
                      proPacksIVRData: {
                        ivrResult: { form_id: latestIvr.id, success: true },
                        formData: latestIvr.form_data,
                        selectedKit: latestIvr.form_data.selectedKitOption
                      },
                      woundDetails: {
                        type: order.wound_type || '',
                        location: order.wound_location || '',
                        drainage: capitalizeFirst(order.wound_drainage),
                        length: order.wound_length || '',
                        width: order.wound_width || '',
                        depth: order.wound_depth || '',
                        chronic: order.is_chronic_wound || false,
                        conservative_care: true,
                        is_full_thickness: true,
                        failed_conservative_care: null,
                        icd10: '',
                      }
                    });
                  }
                } catch (err) {
                  console.error('❌ Error fetching IVR form data:', err);
                  alert('Failed to load IVR data. Please try again.');
                }
                return;
              }

              // Advanced care — existing behavior
              if (onNavigateToAddPatient) {
                if (onNavigateToAddPatient) {
                  onNavigateToAddPatient({
                    patientId: order.patient_id,
                    patient: patient,
                    careType: order.care_type,
                    skipToStep2: false,
                    providerId: selectedPendingOrdersProvider.id,
                    partialOrderId: order.id,
                    isCompletingPartialOrder: true,
                    woundDetails: {
                      type: order.wound_type || '',
                      location: order.wound_location || '',
                      drainage: capitalizeFirst(order.wound_drainage),
                      length: order.wound_length || '',
                      width: order.wound_width || '',
                      depth: order.wound_depth || '',
                      chronic: order.is_chronic_wound || false,
                      conservative_care: order.care_type === 'propacks',
                      is_full_thickness: true,
                      failed_conservative_care: null,
                      icd10: '',
                    },
                    skipToStep3: true
                  });
                }
              }
            }}
            fullPageMode={true}
          />
        </motion.div>
      </div>
    </div>
  );
};

  return (
    <>
      <AnimatePresence mode="wait">
        {currentView === 'dashboard' && renderDashboard()}
        {currentView === 'pendingOrdersProviderSelection' && renderPendingOrdersProviderSelection()} {/* ✅ ADD */}
 
        {currentView === 'pendingPartialOrders' && renderPendingOrdersPage()}
        {currentView === 'reorderSuccess' && renderReorderSuccess()}
      </AnimatePresence>

      <ReorderConfirmationModal />
    </>
  );
};

export default SalesRepAdvancedCareDashboard;